import React from 'react';
import styled from 'styled-components';
import { CheckOutlined, LockOutlined } from '@ant-design/icons';
import { MOBILE_BREAKPOINT } from '../../../constants/mobile';

export type StepState = 'done' | 'active' | 'locked';

interface Props {
  index: number;
  title: string;
  /** One line under the title: what this step is for, or what it holds once done. */
  subtitle?: React.ReactNode;
  state: StepState;
  /** Shown instead of the content while locked: what to do first. */
  lockedHint?: string;
  /** Right side of the header, e.g. the step's own save button. */
  extra?: React.ReactNode;
  children?: React.ReactNode;
}

const mobile = `@media (max-width: ${MOBILE_BREAKPOINT - 1}px)`;

const Wrap = styled.section<{ $state: StepState }>`
  position: relative;
  padding-left: 44px;
  padding-bottom: 20px;

  /* the line joining one step to the next */
  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 34px;
    bottom: 0;
    width: 2px;
    background: ${(p) => (p.$state === 'done' ? 'rgba(14, 203, 129, 0.45)' : '#2b3139')};
  }
  &:last-child::before {
    display: none;
  }

  ${mobile} {
    padding-left: 38px;
    padding-bottom: 16px;
  }
`;

const Badge = styled.div<{ $state: StepState }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => (p.$state === 'locked' ? '#5e6673' : p.$state === 'done' ? '#0b0e11' : '#0ecb81')};
  background: ${(p) => (p.$state === 'done' ? '#0ecb81' : '#181a20')};
  border: 2px solid ${(p) => (p.$state === 'locked' ? '#2b3139' : '#0ecb81')};

  ${mobile} {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Title = styled.div<{ $state: StepState }>`
  min-width: 0;

  .t {
    font-size: 16px;
    font-weight: 600;
    color: ${(p) => (p.$state === 'locked' ? '#5e6673' : '#eaecef')};
    line-height: 32px;
  }
  .s {
    font-size: 13px;
    color: #848e9c;
    margin-top: -2px;
  }
`;

const Locked = styled.div`
  padding: 14px 16px;
  border: 1px dashed #2b3139;
  border-radius: 8px;
  color: #5e6673;
  font-size: 13px;
`;

/** One numbered step of a flow; a later step stays locked until the one before it is done. */
const FlowStep: React.FC<Props> = ({ index, title, subtitle, state, lockedHint, extra, children }) => (
  <Wrap $state={state} aria-label={`Step ${index}: ${title}`} data-state={state}>
    <Badge $state={state}>{state === 'done' ? <CheckOutlined /> : state === 'locked' ? <LockOutlined /> : index}</Badge>
    <Head>
      <Title $state={state}>
        <div className="t">{title}</div>
        {subtitle && <div className="s">{subtitle}</div>}
      </Title>
      {state !== 'locked' && extra}
    </Head>
    {state === 'locked' ? <Locked>{lockedHint}</Locked> : children}
  </Wrap>
);

export default FlowStep;
